import { z } from "zod/v4-mini";

import { TandemCommand, TandemResponse } from "@/lib/drivers/tandem/TandemTypes";
import { Schema } from "@/utils/zodUtils";

export type GlobalDataPayload = {
  quickbolus_active: number; // 0=off, 1=active
  quickbolus_units: number; // 0.001u
  quickbolus_carbs: number; // 0.001 g
  quickbolus_iscarbs: number; // 0=insulin, 1=carbs
  quickbolus_status: number; // bit 0,1,2,3 for active, carbs, units, entry_type, respectively
  button_annun: number;
  quickbolus_annun: number;
  bolus_annun: number;
  reminder_annun: number;
  alert_annun: number;
  alarm_annun: number;
};

export type GlobalData = {
  quickBolusActive: boolean;
  quickBolusUnits: number;
  quickBolusCarbs: number;
  quickBolusType: "carbs" | "units";
  quickBolusStatus: "active" | "carbs" | "units" | "entry_type";
  buttonAnnun: number;
  quickBolusAnnun: number;
  bolusAnnun: number;
  reminderAnnun: number;
  alertAnnun: number;
  alarmAnnun: number;
};

export const globalDataPayloadSchema: Schema<GlobalDataPayload> = z.object({
  quickbolus_active: z.number(),
  quickbolus_units: z.number(),
  quickbolus_carbs: z.number(),
  quickbolus_iscarbs: z.number(),
  quickbolus_status: z.number(),
  button_annun: z.number(),
  quickbolus_annun: z.number(),
  bolus_annun: z.number(),
  reminder_annun: z.number(),
  alert_annun: z.number(),
  alarm_annun: z.number(),
});

export const GLOBAL_DATA_RESPONSE = {
  value: 179,
  name: "Globals Report Message",
  version: 36102,
  format: "bhhbbbbbbbb",
  fields: [
    "quickbolus_active", // 0=off, 1=active
    "quickbolus_units", // 0.001u
    "quickbolus_carbs", // 0.001 g
    "quickbolus_iscarbs", // 0=insulin, 1=carbs
    "quickbolus_status", // bit 0,1,2,3 for active, carbs, units, entry_type, respectively
    "button_annun",
    "quickbolus_annun",
    "bolus_annun",
    "reminder_annun",
    "alert_annun",
    "alarm_annun",
  ] as const,
  postprocess: rec => ({
    quickBolusActive: rec.quickbolus_active === 1,
    quickBolusUnits: rec.quickbolus_units * 0.001,
    quickBolusCarbs: rec.quickbolus_carbs * 0.001,
    quickBolusType: rec.quickbolus_iscarbs === 1 ? "carbs" : "units",
    quickBolusStatus:
      rec.quickbolus_status === 0
        ? "active"
        : rec.quickbolus_status === 1
          ? "carbs"
          : rec.quickbolus_status === 2
            ? "units"
            : "entry_type",
    buttonAnnun: rec.button_annun,
    quickBolusAnnun: rec.quickbolus_annun,
    bolusAnnun: rec.bolus_annun,
    reminderAnnun: rec.reminder_annun,
    alertAnnun: rec.alert_annun,
    alarmAnnun: rec.alarm_annun,
  }),
  schema: globalDataPayloadSchema,
} as const satisfies TandemResponse<GlobalDataPayload, GlobalData>;

export const GLOBAL_DATA_COMMAND = {
  value: 178,
  name: "Global Data Request",
  version: 36102,
  response: GLOBAL_DATA_RESPONSE,
} as const satisfies TandemCommand<GlobalDataPayload, GlobalData>;
